import {
	CalendarIcon,
	CheckCircleIcon,
	DocumentTextIcon,
	EyeIcon,
} from '@heroicons/react/20/solid';
import { formatDateTime } from '../../common/formatDateText';
import { onlyInvoice } from '../../static/data';
import { Tooltip } from '../../components/smartTable/Tooltip';

const getFullName = (user) => {
	if (!user) return '-';
	return `${user.firstName || ''} ${user.secondName || ''} ${user.lastName || ''} ${user.secondSurname || ''}`.trim() || '-';
};

const StatusBadge = ({ value }) => {
	const status = String(value || '').toLowerCase();
	if (!status) return <span className='text-gray-500'>-</span>;

	const styles = {
		pending: 'bg-orange-100 text-orange-700 border-orange-200',
		done: 'bg-emerald-100 text-emerald-700 border-emerald-200',
		declined: 'bg-rose-100 text-rose-700 border-rose-200',
	};

	const labels = {
		pending: 'Pendiente',
		done: 'Completada',
		declined: 'Rechazada',
	};

	const className = styles[status] || 'bg-slate-100 text-slate-700 border-slate-200';
	const label = labels[status] || status;

	return <span className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-semibold ${className}`}>{label}</span>;
};

export const buildRequestInvoiceColumns = ({ onOpenDetails, onConfirm, typeUser }) => [
	{
		accessorKey: 'numberRequest',
		id: 'numberRequest',
		header: 'No. solicitud',
		size: 180,
		cell: ({ getValue }) => (
			<span className='inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm font-semibold text-slate-700'>
				<DocumentTextIcon className='h-4 w-4 text-slate-500' />
				{getValue() || '-'}
			</span>
		),
		meta: { label: 'No. solicitud', searchable: true, searchPlaceholder: 'Buscar no. solicitud...' },
	},
	{
		id: 'matricula',
		header: 'Matrícula',
		accessorFn: (row) => row.requestBy?.matricula || '',
		size: 140,
		cell: ({ getValue }) => (
			<p className='w-[90px] rounded-md border border-gray-300 px-2 py-1 text-center font-semibold text-gray-700'>
				{getValue() || '-'}
			</p>
		),
		meta: { label: 'Matrícula', searchable: true, searchPlaceholder: 'Buscar matrícula...' },
	},
	{
		id: 'studentName',
		header: 'Estudiante',
		accessorFn: (row) => getFullName(row.requestBy),
		size: 300,
		cell: ({ getValue, row }) => {
			const name = getValue() || '-';
			const email = row.original.requestBy?.email;
			return (
				<div className='min-w-0'>
					<p className='truncate text-sm font-semibold capitalize text-slate-700'>{name}</p>
					{email ? (
						<p className='truncate text-xs text-slate-500' title={email}>
							{email}
						</p>
					) : (
						<p className='truncate text-xs text-slate-400'>-</p>
					)}
				</div>
			);
		},
		meta: {
			label: 'Estudiante',
			searchable: true,
			searchPlaceholder: 'Buscar por nombre o correo...',
		},
	},
	{
		accessorKey: 'requestStatus',
		id: 'requestStatus',
		header: 'Estatus',
		size: 140,
		cell: ({ getValue }) => <StatusBadge value={getValue()} />,
		meta: { label: 'Estatus' },
	},
	{
		id: 'createdAt',
		accessorKey: 'createdAt',
		header: () => (
			<div className='flex items-center gap-2'>
				<CalendarIcon className='h-4 w-4 text-gray-900' />
				<span>Fecha de solicitud</span>
			</div>
		),
		size: 240,
		cell: ({ getValue }) => (
			<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>{formatDateTime(getValue())}</p>
		),
		meta: { label: 'Fecha de solicitud' },
		sortable: false,
		enableSorting: false,
	},
	{
		id: 'account',
		header: 'Cuenta',
		accessorFn: (row) => row.payment?.account?.nameAccount || '-',
		size: 240,
		enableSorting: false,
		cell: ({ row }) => {
			const account = row.original.payment?.account;
			const name = account?.nameAccount || '-';
			const number = account?.numberAccount;
			const icon = account?.urlName;
			return (
				<div className='flex min-w-0 items-start gap-2'>
					{icon ? (
						<img className='mt-0.5 h-7 w-7 shrink-0 rounded object-contain' src={icon} alt='' />
					) : null}
					<div className='min-w-0 flex-1'>
						<p className='truncate text-sm font-semibold uppercase text-slate-700'>{name}</p>
						<p className='truncate text-xs text-slate-500'>{number || '-'}</p>
					</div>
				</div>
			);
		},
		meta: { label: 'Cuenta' },
	},
	{
		id: 'amount',
		header: 'Monto',
		accessorFn: (row) => row.payment?.amount || 0,
		size: 250,
		cell: ({ getValue }) => (
			<span className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>
				{`$ ${getValue() || 0}`}
			</span>
		),
		meta: { label: 'Monto' },
		enableSorting: false,
	},
	{
		id: 'approvedBy',
		header: 'Aprobada por',
		accessorFn: (row) => getFullName(row.approvedBy),
		size: 260,
		enableSorting: false,
		cell: ({ row }) => <p className='truncate text-sm font-semibold capitalize text-slate-700'>{getFullName(row.original.approvedBy)}</p>,
		meta: { label: 'Aprobada por' },
	},
	{
		id: 'businessName',
		header: 'Razón social',
		accessorFn: (row) => row.requestBy?.businessName || '',
		size: 240,
		enableSorting: false,
		cell: ({ getValue }) => <p className='truncate text-sm text-slate-700 uppercase'>{getValue() || '-'}</p>,
		meta: { label: 'Razón social' },
	},
	{
		id: 'rfc',
		header: 'RFC',
		accessorFn: (row) => row.requestBy?.rfc || '',
		size: 170,
		enableSorting: false,
		cell: ({ getValue }) => <p className='text-sm font-medium text-slate-700 uppercase'>{getValue() || '-'}</p>,
		meta: { label: 'RFC' },
	},
	{
		id: 'emailTax',
		header: 'Correo fiscal',
		accessorFn: (row) => row.requestBy?.emailTax || '',
		size: 260,
		enableSorting: false,
		cell: ({ getValue }) => <p className='truncate text-sm text-slate-700'>{getValue() || '-'}</p>,
		meta: { label: 'Correo fiscal' },
	},
	{
		id: 'taxRegime',
		header: 'Régimen fiscal',
		accessorFn: (row) => row.requestBy?.taxRegime || '',
		size: 210,
		enableSorting: false,
		cell: ({ getValue }) => <p className='truncate text-sm text-slate-700 capitalize'>{getValue() || '-'}</p>,
		meta: { label: 'Régimen fiscal' },
	},
	{
		id: 'phone',
		header: 'Teléfono',
		accessorFn: (row) => row.requestBy?.phone || '',
		size: 300,
		enableSorting: false,
		cell: ({ getValue }) => <p className='text-sm text-slate-700'>{getValue() || '-'}</p>,
		meta: { label: 'Teléfono' },
	},
	{
		id: 'action',
		header: () => null,
		enableSorting: false,
		enableHiding: false,
		size: 220,
		minSize: 220,
		cell: ({ row }) => {
			const canConfirm = row.original.requestStatus === 'pending' && onlyInvoice.includes(typeUser);
			return (
				<div className='flex justify-center gap-2'>
					<Tooltip title='Ver detalle de la solicitud' placement='left'>
						<button
							type='button'
							onClick={() => onOpenDetails(row.original._id)}
							className='inline-flex min-w-[110px] items-center justify-center gap-1.5 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-300'
						>
							<EyeIcon className='h-4 w-4' aria-hidden='true' />
							Detalle
						</button>
					</Tooltip>
					{canConfirm ? (
						<Tooltip title='Confirmar solicitud de factura' placement='left'>
							<button
								type='button'
								onClick={() => onConfirm(row.original._id)}
								className='inline-flex min-w-[110px] items-center justify-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700'
							>
								<CheckCircleIcon className='h-4 w-4' aria-hidden='true' />
								Confirmar
							</button>
						</Tooltip>
					) : (
						<button
							type='button'
							disabled
							aria-hidden='true'
							className='invisible inline-flex min-w-[110px] items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold'
						>
							<CheckCircleIcon className='h-4 w-4' aria-hidden='true' />
							Confirmar
						</button>
					)}
				</div>
			);
		},
		meta: { label: 'Acciones', sticky: 'right' },
	},
];
